import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IAllCompleteHealthCare,
  IAllHealthCareFetchResponse,
  IBillingUrlFormResponse,
  IDeleteCommonHealthCareResponse,
  IDeleteHealthCareRequiredDocumentResponse,
  IDepartmentResponse,
  IDesignationResponse,
  IFetchAllBillingUrlFormResponse,
  IFetchAllHealthCareDoctorResponse,
  IFetchAllHealthCareSchemeResponse,
  IFetchAllSchemeSpecificLoginDetailsResponse,
  IFetchManageUserResponse,
  IHealthCare,
  IHealthCareDoctorForm,
  IHealthCareFilter,
  IHealthCareRequiredDocument,
  IHealthCareResponse,
  IHealthCareScheme,
  IHealthCareState,
  IManageUserForm,
  IPatchHealthCareScheme,
  IPortalResponse,
  IPostBillingUrlFormResponse,
  IPostHealthCareDoctorResponse,
  IPostHealthCareSchemeResponse,
  IPostManageUserResponse,
  IPostSchemeSpecificLoginDetailsResponse,
  IQualificationResponse,
  ISchemeSpecificLoginDetailsForm,
} from "../interfaces/IHealthCare";
import {
  deleteBillingUrlById,
  deleteHealthCareDoctorById,
  deleteHealthCareRequiredDocumentById,
  deleteHealthCareUserById,
  deleteSchemeSpecificLoginDetailById,
  getAllBillingUrlForm,
  getAllDepartments,
  getAllDesignations,
  getAllHealthCareDoctor,
  getAllHealthCareScheme,
  getAllManageUsers,
  getAllPortals,
  getAllQualifications,
  getAllSchemes,
  getAllSchemeSpecificLoginDetails,
  getHealthCareById,
  getHealthCares,
  postSubmitBillingUrlForm,
  postSubmitHealthCare,
  postSubmitHealthCareDoctor,
  postSubmitHealthCareRequiredDocument,
  postSubmitHealthCareScheme,
  postSubmitHealthCareUser,
  postSubmitSchemeSpecificLoginDetails,
  updateHealthCareBillingUrlById,
  updateHealthCareById,
  updateHealthCareDoctorById,
  updateHealthCareRequiredDocumentById,
  updateHealthCareSchemeById,
  updateHealthCareSchemeSpecificLoginDetailById,
  updateHealthCareUserById,
} from "../utils/healthCareSliceFunc";
import { ISchemeResponse } from "../interfaces/IScheme";

const initialState: IHealthCareState = {
  isSubmittingHealthCare: false,
  isSubmittingHealthCareScheme: false,
  isSubmittingHealthCareDoctor: false,
  isSubmittingHealthCareUser: false,
  isSubmittingSchemeSpecificLoginDetail: false,
  isSubmittingBillingUrl: false,
  isSubmittingHealthCareRequiredDoc: false,
  isUpdatingHealthCare: false,
  isUpdatingHealthCareScheme: false,
  isUpdatingHealthCareUser: false,
  isUpdatingHealthCareDoctor: false,
  isUpdatingHealthCareBillingUrl: false,
  isUpdatingHealthCareSchemeSpecificLoginDetail: false,
  isUpdatingHealthCareRequiredDocumentById: false,
  selectedHealthCare: {
    healthCare: null,
    healthCareScheme: null,
    healthCareDoctor: null,
    healthCareManagingUser: null,
    healthCareSchemeSpecificLogin: null,
    healthCareBillingUrl: null,
    healthCareRequiredDocuments: null,
  },
  isFetchingHealthCareById: false,
  loading: false,
  error: null,
  totalAvailableHealthCares: 0,
  healthCares: {},
  healthCareSchemes: {},
  healthCareDoctors: {},
  healthCareUsers: {},
  healthCareSchemeSpecificLoginDetails: {},
  healthCareBillingUrls: {},
  healthCareRequiredDocs: {},
  isFetchingAllHealthCares: false,
  isFetchingAllHealthCareSchemes: false,
  isFetchingAllHealthCareDoctors: false,
  isFetchingAllHealthCareUsers: false,
  isFetchingAllHealthCareSchemeSpecificLoginDetails: false,
  isFetchingAllHealthCareBillingUrls: false,
  isFetchingAllHealthCareRequiredDocs: false,
  isFetchingPortals: false,
  isFetchingDepartments: false,
  isFetchingSchemes: false,
  isFetchingDesignations: false,
  isFetchingQualifications: false,
  healthCareFilters: {
    searchTerm: null,
    orderBy: "DESC",
    pageNumber: 1,
    pageSize: 10,
    startDate: "",
    endDate: "",
  },
  pageToHealthCareIdMap: {},
  portals: {},
  departments: {},
  schemes: {},
  designations: {},
  qualifications: {},
};

const healthCareSlice = createSlice({
  name: "healthCares",
  initialState,
  reducers: {
    setSelectedHealthCare: (state, action: PayloadAction<IHealthCare>) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCare = action.payload;
    },
    setSelectedHealthCareScheme: (
      state,
      action: PayloadAction<IHealthCareScheme>
    ) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareScheme = action.payload;
    },
    setSelectedHealthCareDoctor: (
      state,
      action: PayloadAction<IHealthCareDoctorForm>
    ) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareDoctor = action.payload;
    },
    setSelectedHealthCareUser: (
      state,
      action: PayloadAction<IManageUserForm>
    ) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareManagingUser = action.payload;
    },
    setSelectedHealthCareSchemeSpecificLoginDetail: (
      state,
      action: PayloadAction<ISchemeSpecificLoginDetailsForm>
    ) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareSchemeSpecificLogin = action.payload;
    },
    setSelectedHealthCareBillingUrl: (
      state,
      action: PayloadAction<IBillingUrlFormResponse>
    ) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareBillingUrl = action.payload;
    },
    setSelectedHealthCareRequiredDoc: (
      state,
      action: PayloadAction<IHealthCareRequiredDocument>
    ) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareRequiredDocuments = action.payload;
    },
    clearHealthCareList: (state) => {
      if (state.healthCares) state.healthCares = {};
    },
    clearSelectedHealthCare: (state) => {
      if (state.selectedHealthCare) state.selectedHealthCare.healthCare = null;
    },
    clearSelectedHealthCareScheme: (state) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareScheme = null;
    },
    clearSelectedHealthCareDoctor: (state) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareDoctor = null;
    },
    clearSelectedHealthCareUser: (state) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareManagingUser = null;
    },
    clearSelectedHealthCareSchemeSpecificLoginDetail: (state) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareSchemeSpecificLogin = null;
    },
    clearSelectedHealthCareBillingUrl: (state) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareBillingUrl = null;
    },
    clearSelectedHealthCareRequiredDoc: (state) => {
      if (state.selectedHealthCare)
        state.selectedHealthCare.healthCareRequiredDocuments = null;
    },
    clearHealthCareSchemesDoctorsUsersLoginDetailsUrlRequiredDocs: (state) => {
      if (state.healthCareSchemes) {
        state.healthCareSchemes = {};
      }
      if (state.healthCareDoctors) {
        state.healthCareDoctors = {};
      }
      if (state.healthCareUsers) {
        state.healthCareUsers = {};
      }
      if (state.healthCareSchemeSpecificLoginDetails) {
        state.healthCareSchemeSpecificLoginDetails = {};
      }
      if (state.healthCareBillingUrls) {
        state.healthCareBillingUrls = {};
      }
      if (state.healthCareRequiredDocs) {
        state.healthCareRequiredDocs = {};
      }
    },
    setHealthCareFilters: (state, action: PayloadAction<IHealthCareFilter>) => {
      state.healthCareFilters = action.payload;
    },
    clearPageToHealthCareIdMap: (state) => {
      state.pageToHealthCareIdMap = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHealthCares.pending, (state) => {
        state.isFetchingAllHealthCares = true;
        state.error = null;
      })
      .addCase(
        getHealthCares.fulfilled,
        (state, action: PayloadAction<IAllHealthCareFetchResponse>) => {
          state.isFetchingAllHealthCares = false;
          action.payload.healthCareResponseList?.forEach((healthCare) => {
            if (healthCare?.id) {
              state.healthCares[healthCare.id] = healthCare;

              if (
                !state.pageToHealthCareIdMap[state.healthCareFilters.pageNumber]
              ) {
                state.pageToHealthCareIdMap[
                  state.healthCareFilters.pageNumber
                ] = [];
              }
              if (
                !state.pageToHealthCareIdMap[
                  state.healthCareFilters.pageNumber
                ].includes(healthCare.id)
              ) {
                state.pageToHealthCareIdMap[
                  state.healthCareFilters.pageNumber
                ].push(healthCare.id);
              }
            }
          });

          state.totalAvailableHealthCares = action.payload.totalElements || 0;
          state.healthCareFilters.pageSize = action.payload.totalPages || 10;
          state.error = null;
        }
      )
      .addCase(getHealthCares.rejected, (state, action) => {
        state.isFetchingAllHealthCares = false;
        state.error = action.error.message || "Failed to fetch health cares";
      })
      .addCase(postSubmitHealthCare.pending, (state) => {
        state.isSubmittingHealthCare = true;
        state.error = null;
      })
      .addCase(
        postSubmitHealthCare.fulfilled,
        (state, action: PayloadAction<IHealthCareResponse>) => {
          if (state.selectedHealthCare && action.payload.healthCareRes) {
            state.selectedHealthCare.healthCare = action.payload.healthCareRes;
            state.isSubmittingHealthCare = false;
            state.error =
              action.payload.errorMessage || "Failed to post health care";
          }
        }
      )
      .addCase(postSubmitHealthCare.rejected, (state, action) => {
        state.isSubmittingHealthCare = false;
        state.error = action.error.message || "Failed to post health care";
      })

      .addCase(postSubmitHealthCareDoctor.pending, (state) => {
        state.isSubmittingHealthCareDoctor = true;
        state.error = null;
      })
      .addCase(
        postSubmitHealthCareDoctor.fulfilled,
        (state, action: PayloadAction<IPostHealthCareDoctorResponse>) => {
          if (state.selectedHealthCare && action.payload.doctorRes) {
            state.selectedHealthCare.healthCareDoctor =
              action.payload.doctorRes;
            state.isSubmittingHealthCareDoctor = false;
            state.error =
              action.payload.errorMessage ||
              "Failed to post health care doctor";
          }
        }
      )
      .addCase(postSubmitHealthCareDoctor.rejected, (state, action) => {
        state.isSubmittingHealthCareDoctor = false;
        state.error =
          action.error.message || "Failed to post health care doctor";
      })

      .addCase(postSubmitHealthCareUser.pending, (state) => {
        state.isSubmittingHealthCareUser = true;
        state.error = null;
      })
      .addCase(
        postSubmitHealthCareUser.fulfilled,
        (state, action: PayloadAction<IPostManageUserResponse>) => {
          if (state.selectedHealthCare && action.payload.manageUserRes) {
            state.selectedHealthCare.healthCareManagingUser =
              action.payload.manageUserRes;
            state.isSubmittingHealthCareUser = false;
            state.error =
              action.payload.errorMessage || "Failed to post health care user";
          }
        }
      )
      .addCase(postSubmitHealthCareUser.rejected, (state, action) => {
        state.isSubmittingHealthCareUser = false;
        state.error = action.error.message || "Failed to post health care user";
      })

      .addCase(postSubmitSchemeSpecificLoginDetails.pending, (state) => {
        state.isSubmittingSchemeSpecificLoginDetail = true;
        state.error = null;
      })
      .addCase(
        postSubmitSchemeSpecificLoginDetails.fulfilled,
        (
          state,
          action: PayloadAction<IPostSchemeSpecificLoginDetailsResponse>
        ) => {
          if (
            state.selectedHealthCare &&
            action.payload.schemeSpecificLoginDetailsRes
          ) {
            state.selectedHealthCare.healthCareSchemeSpecificLogin =
              action.payload.schemeSpecificLoginDetailsRes;
            state.isSubmittingSchemeSpecificLoginDetail = false;
            state.error =
              action.payload.errorMessage ||
              "Failed to post scheme specific login detail";
          }
        }
      )
      .addCase(
        postSubmitSchemeSpecificLoginDetails.rejected,
        (state, action) => {
          state.isSubmittingSchemeSpecificLoginDetail = false;
          state.error =
            action.error.message ||
            "Failed to post scheme specific login detail";
        }
      )

      .addCase(postSubmitBillingUrlForm.pending, (state) => {
        state.isSubmittingBillingUrl = true;
        state.error = null;
      })
      .addCase(
        postSubmitBillingUrlForm.fulfilled,
        (state, action: PayloadAction<IPostBillingUrlFormResponse>) => {
          if (state.selectedHealthCare && action.payload.billingUrlFormRes) {
            state.selectedHealthCare.healthCareBillingUrl =
              action.payload.billingUrlFormRes;
            state.isSubmittingBillingUrl = false;
            state.error =
              action.payload.errorMessage ||
              "Failed to post health care billing url";
          }
        }
      )
      .addCase(postSubmitBillingUrlForm.rejected, (state, action) => {
        state.isSubmittingBillingUrl = false;
        state.error =
          action.error.message || "Failed to post health care billing url";
      })
      .addCase(postSubmitHealthCareRequiredDocument.pending, (state) => {
        state.isSubmittingHealthCareRequiredDoc = true;
        state.error = null;
      })
      .addCase(
        postSubmitHealthCareRequiredDocument.fulfilled,
        (state, action: PayloadAction<IAllCompleteHealthCare>) => {
          if (
            state.selectedHealthCare &&
            action.payload.healthCareRequiredDocumentsRes &&
            action.payload.healthCareRequiredDocumentsRes.length > 0
          ) {
            state.selectedHealthCare.healthCareRequiredDocuments =
              action.payload.healthCareRequiredDocumentsRes[
                action.payload.healthCareRequiredDocumentsRes.length - 1
              ];
            state.isSubmittingHealthCareRequiredDoc = false;
            state.error =
              action.payload.errorMessage ||
              "Failed to post health care required document";
          }
        }
      )
      .addCase(
        postSubmitHealthCareRequiredDocument.rejected,
        (state, action) => {
          state.isSubmittingHealthCareRequiredDoc = false;
          state.error =
            action.error.message ||
            "Failed to post health care required document";
        }
      )

      .addCase(getHealthCareById.pending, (state) => {
        state.isFetchingAllHealthCareRequiredDocs = true;
        state.error = null;
      })
      .addCase(
        getHealthCareById.fulfilled,
        (state, action: PayloadAction<IAllCompleteHealthCare>) => {
          if (
            !action.payload.healthCareRequiredDocumentsRes ||
            action.payload.healthCareRequiredDocumentsRes.length === 0
          ) {
            state.healthCareRequiredDocs = {};
          } else {
            action.payload.healthCareRequiredDocumentsRes?.forEach(
              (requiredDoc) => {
                if (requiredDoc?.id) {
                  state.healthCareRequiredDocs[requiredDoc.id] = requiredDoc;
                }
              }
            );
          }
          state.isFetchingAllHealthCareRequiredDocs = false;
          state.error =
            action.payload.errorMessage ||
            "Failed to get all health care required documents";
        }
      )
      .addCase(getHealthCareById.rejected, (state, action) => {
        state.isFetchingAllHealthCareRequiredDocs = false;
        state.error =
          action.error.message ||
          "Failed to get all health care required documents";
      })

      .addCase(getAllSchemeSpecificLoginDetails.pending, (state) => {
        state.isFetchingAllHealthCareSchemeSpecificLoginDetails = true;
        state.error = null;
      })
      .addCase(
        getAllSchemeSpecificLoginDetails.fulfilled,
        (
          state,
          action: PayloadAction<IFetchAllSchemeSpecificLoginDetailsResponse>
        ) => {
          if (
            !action.payload.schemeSpecificLoginDetailsRes ||
            action.payload.schemeSpecificLoginDetailsRes.length === 0
          ) {
            state.healthCareSchemeSpecificLoginDetails = {};
          } else {
            action.payload.schemeSpecificLoginDetailsRes?.forEach(
              (loginDetail) => {
                if (loginDetail?.id) {
                  state.healthCareSchemeSpecificLoginDetails[loginDetail.id] =
                    loginDetail;
                }
              }
            );
          }
          state.isFetchingAllHealthCareSchemeSpecificLoginDetails = false;
          state.error =
            action.payload.errorMessage ||
            "Failed to get all health care scheme specific login details";
        }
      )
      .addCase(getAllSchemeSpecificLoginDetails.rejected, (state, action) => {
        state.isFetchingAllHealthCareSchemeSpecificLoginDetails = false;
        state.error =
          action.error.message ||
          "Failed to get all health care scheme specific login details";
      })

      .addCase(getAllBillingUrlForm.pending, (state) => {
        state.isFetchingAllHealthCareBillingUrls = true;
        state.error = null;
      })
      .addCase(
        getAllBillingUrlForm.fulfilled,
        (state, action: PayloadAction<IFetchAllBillingUrlFormResponse>) => {
          if (
            !action.payload.billingUrlFormRes ||
            action.payload.billingUrlFormRes.length === 0
          ) {
            state.healthCareBillingUrls = {};
          } else {
            action.payload.billingUrlFormRes?.forEach((billingUrl) => {
              if (billingUrl?.id) {
                state.healthCareBillingUrls[billingUrl.id] = billingUrl;
              }
            });
          }
          state.isFetchingAllHealthCareBillingUrls = false;
          state.error =
            action.payload.errorMessage ||
            "Failed to get all health care billing url form";
        }
      )
      .addCase(getAllBillingUrlForm.rejected, (state, action) => {
        state.isFetchingAllHealthCareBillingUrls = false;
        state.error =
          action.error.message ||
          "Failed to get all health care billing url form";
      })

      .addCase(postSubmitHealthCareScheme.pending, (state) => {
        state.isSubmittingHealthCareScheme = true;
        state.error = null;
      })
      .addCase(
        postSubmitHealthCareScheme.fulfilled,
        (state, action: PayloadAction<IPostHealthCareSchemeResponse>) => {
          if (state.selectedHealthCare && action.payload.schemeRes) {
            state.selectedHealthCare.healthCareScheme =
              action.payload.schemeRes;
            state.isSubmittingHealthCareScheme = false;
            state.error =
              action.payload.errorMessage ||
              "Failed to post health care scheme";
          }
        }
      )
      .addCase(postSubmitHealthCareScheme.rejected, (state, action) => {
        state.isSubmittingHealthCareScheme = false;
        state.error =
          action.error.message || "Failed to post health care scheme";
      })
      .addCase(getAllHealthCareScheme.pending, (state) => {
        state.isFetchingAllHealthCareSchemes = true;
        state.error = null;
      })
      .addCase(
        getAllHealthCareScheme.fulfilled,
        (state, action: PayloadAction<IFetchAllHealthCareSchemeResponse>) => {
          if (
            !action.payload.schemeRes ||
            action.payload.schemeRes.length === 0
          ) {
            state.healthCareSchemes = {};
          } else {
            action.payload.schemeRes?.forEach((scheme) => {
              if (scheme?.id) {
                state.healthCareSchemes[scheme.id] = scheme;
              }
            });
          }
          state.isFetchingAllHealthCareSchemes = false;
          state.error =
            action.payload.errorMessage ||
            "Failed to get all health care schemes";
        }
      )
      .addCase(getAllHealthCareScheme.rejected, (state, action) => {
        state.isFetchingAllHealthCareSchemes = false;
        state.error =
          action.error.message || "Failed to get all health care schemes";
      })
      .addCase(getAllHealthCareDoctor.pending, (state) => {
        state.isFetchingAllHealthCareDoctors = true;
        state.error = null;
      })
      .addCase(
        getAllHealthCareDoctor.fulfilled,
        (state, action: PayloadAction<IFetchAllHealthCareDoctorResponse>) => {
          if (
            !action.payload.doctorRes ||
            action.payload.doctorRes.length === 0
          ) {
            state.healthCareDoctors = {};
          } else {
            action.payload.doctorRes?.forEach((doctor) => {
              if (doctor?.id) {
                state.healthCareDoctors[doctor.id] = doctor;
              }
            });
          }
          state.isFetchingAllHealthCareDoctors = false;
          state.error =
            action.payload.errorMessage ||
            "Failed to get all health care doctors";
        }
      )
      .addCase(getAllHealthCareDoctor.rejected, (state, action) => {
        state.isFetchingAllHealthCareDoctors = false;
        state.error =
          action.error.message || "Failed to get all health care doctors";
      })
      .addCase(getAllManageUsers.pending, (state) => {
        state.isFetchingAllHealthCareUsers = true;
        state.error = null;
      })
      .addCase(
        getAllManageUsers.fulfilled,
        (state, action: PayloadAction<IFetchManageUserResponse>) => {
          if (
            !action.payload.manageUserRes ||
            action.payload.manageUserRes.length === 0
          ) {
            state.healthCareUsers = {};
          } else {
            action.payload.manageUserRes?.forEach((user) => {
              if (user?.id) {
                state.healthCareUsers[user.id] = user;
              }
            });
          }
          state.isFetchingAllHealthCareUsers = false;
          state.error =
            action.payload.errorMessage ||
            "Failed to get all health care users";
        }
      )
      .addCase(getAllManageUsers.rejected, (state, action) => {
        state.isFetchingAllHealthCareUsers = false;
        state.error =
          action.error.message || "Failed to get all health care users";
      })
      .addCase(getAllPortals.pending, (state, action) => {
        state.isFetchingPortals = true;
        state.error = null;
      })
      .addCase(
        getAllPortals.fulfilled,
        (state, action: PayloadAction<IPortalResponse>) => {
          state.isFetchingPortals = false;

          if (action.payload.portals) {
            action.payload.portals?.forEach((portal, idx) => {
              if (portal?.id) {
                state.portals[portal?.id] = portal;
              }
            });
          }

          state.error =
            action.payload.errorMessage || "Failed to fetch all portals";
        }
      )
      .addCase(getAllPortals.rejected, (state, action) => {
        state.isFetchingPortals = false;
        state.error = action.error.message || "Failed to fetch all portals";
      })
      .addCase(getAllDepartments.pending, (state, action) => {
        state.isFetchingDepartments = true;
        state.error = null;
      })
      .addCase(
        getAllDepartments.fulfilled,
        (state, action: PayloadAction<IDepartmentResponse>) => {
          state.isFetchingDepartments = false;
          if (action.payload.departments) {
            action.payload.departments?.forEach((department, idx) => {
              if (department?.id) {
                state.departments[department?.id] = department;
              }
            });
          }
          state.error =
            action.payload.errorMessage || "Failed to fetch all departments";
        }
      )
      .addCase(getAllDepartments.rejected, (state, action) => {
        state.isFetchingDepartments = false;
        state.error = action.error.message || "Failed to fetch all departments";
      })
      .addCase(getAllSchemes.pending, (state, action) => {
        state.isFetchingSchemes = true;
        state.error = null;
      })
      .addCase(
        getAllSchemes.fulfilled,
        (state, action: PayloadAction<ISchemeResponse>) => {
          state.isFetchingSchemes = false;
          if (action.payload.schemes) {
            action.payload.schemes?.forEach((scheme, idx) => {
              if (scheme?.id) {
                state.schemes[scheme?.id] = scheme;
              }
            });
          }
          state.error =
            action.payload.errorMessage || "Failed to fetch all schemes";
        }
      )
      .addCase(getAllSchemes.rejected, (state, action) => {
        state.isFetchingSchemes = false;
        state.error = action.error.message || "Failed to fetch all schemes";
      })
      .addCase(getAllDesignations.pending, (state, action) => {
        state.isFetchingDesignations = true;
        state.error = null;
      })
      .addCase(
        getAllDesignations.fulfilled,
        (state, action: PayloadAction<IDesignationResponse>) => {
          state.isFetchingDesignations = false;
          if (action.payload.designation) {
            action.payload.designation?.forEach((designation, idx) => {
              if (designation?.id) {
                state.designations[designation?.id] = designation;
              }
            });
          }

          state.error =
            action.payload.errorMessage || "Failed to fetch all designations";
        }
      )
      .addCase(getAllDesignations.rejected, (state, action) => {
        state.isFetchingDesignations = false;
        state.error =
          action.error.message || "Failed to fetch all designations";
      })
      .addCase(getAllQualifications.pending, (state, action) => {
        state.isFetchingQualifications = true;
        state.error = null;
      })
      .addCase(
        getAllQualifications.fulfilled,
        (state, action: PayloadAction<IQualificationResponse>) => {
          state.isFetchingQualifications = false;
          // if (action.payload.qualification) {
          //   state.qualifications = action.payload.qualification;
          // }
          if (action.payload.qualification) {
            action.payload.qualification?.forEach((qualification, idx) => {
              if (qualification?.id) {
                state.qualifications[qualification?.id] = qualification;
              }
            });
          }
          state.error =
            action.payload.errorMessage || "Failed to fetch all qualifications";
        }
      )
      .addCase(getAllQualifications.rejected, (state, action) => {
        state.isFetchingQualifications = false;
        state.error =
          action.error.message || "Failed to fetch all qualifications";
      })
      .addCase(deleteHealthCareDoctorById.pending, (state) => {
        state.error = null;
      })
      .addCase(
        deleteHealthCareDoctorById.fulfilled,
        (state, action: PayloadAction<IDeleteCommonHealthCareResponse>) => {
          const doctorId = action.payload.id;
          if (doctorId !== undefined) {
            delete state.healthCareDoctors[doctorId];
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to delete health care doctor";
        }
      )
      .addCase(deleteHealthCareDoctorById.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to delete health care doctor";
      })
      .addCase(deleteHealthCareUserById.pending, (state) => {
        state.error = null;
      })
      .addCase(
        deleteHealthCareUserById.fulfilled,
        (state, action: PayloadAction<IDeleteCommonHealthCareResponse>) => {
          const userId = action.payload.id;
          if (userId !== undefined) {
            delete state.healthCareUsers[userId];
          }
          state.error =
            action.payload.errorMessage || "Failed to delete health care user";
        }
      )
      .addCase(deleteHealthCareUserById.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to delete health care user";
      })
      .addCase(deleteSchemeSpecificLoginDetailById.pending, (state) => {
        state.error = null;
      })
      .addCase(
        deleteSchemeSpecificLoginDetailById.fulfilled,
        (state, action: PayloadAction<IDeleteCommonHealthCareResponse>) => {
          const loginDetailId = action.payload.id;
          if (loginDetailId !== undefined) {
            delete state.healthCareSchemeSpecificLoginDetails[loginDetailId];
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to delete health care doctor";
        }
      )
      .addCase(
        deleteSchemeSpecificLoginDetailById.rejected,
        (state, action) => {
          state.error =
            action.error.message || "Failed to delete health care doctor";
        }
      )
      .addCase(deleteBillingUrlById.pending, (state) => {
        state.error = null;
      })
      .addCase(
        deleteBillingUrlById.fulfilled,
        (state, action: PayloadAction<IDeleteCommonHealthCareResponse>) => {
          const billingUrlId = action.payload.id;
          if (billingUrlId !== undefined) {
            delete state.healthCareBillingUrls[billingUrlId];
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to delete health care doctor";
        }
      )
      .addCase(deleteBillingUrlById.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to delete health care doctor";
      })
      .addCase(deleteHealthCareRequiredDocumentById.pending, (state) => {
        state.error = null;
      })
      .addCase(
        deleteHealthCareRequiredDocumentById.fulfilled,
        (
          state,
          action: PayloadAction<IDeleteHealthCareRequiredDocumentResponse>
        ) => {
          const requiredDocId = action.payload.id;
          if (requiredDocId !== undefined) {
            delete state.healthCareRequiredDocs[requiredDocId];
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to delete health care required document";
        }
      )
      .addCase(
        deleteHealthCareRequiredDocumentById.rejected,
        (state, action) => {
          state.error =
            action.error.message ||
            "Failed to delete health care required document";
        }
      )

      .addCase(updateHealthCareById.pending, (state) => {
        state.isUpdatingHealthCare = true;
        state.error = null;
      })
      .addCase(
        updateHealthCareById.fulfilled,
        (state, action: PayloadAction<IAllCompleteHealthCare>) => {
          state.isUpdatingHealthCare = false;
          if (state.selectedHealthCare) {
            state.selectedHealthCare.healthCare = action.payload.healthCareRes;
          }
          state.error =
            action.payload.errorMessage || "Failed to update health care";
        }
      )
      .addCase(updateHealthCareById.rejected, (state, action) => {
        state.isUpdatingHealthCare = false;
        state.error = action.error.message || "Failed to update health care";
      })

      .addCase(updateHealthCareSchemeById.pending, (state) => {
        state.isUpdatingHealthCareScheme = true;
        state.error = null;
      })
      .addCase(
        updateHealthCareSchemeById.fulfilled,
        (state, action: PayloadAction<IPatchHealthCareScheme>) => {
          state.isUpdatingHealthCareScheme = false;
          if (
            state.selectedHealthCare &&
            action.payload.updatedHealthCareScheme
          ) {
            state.selectedHealthCare.healthCareScheme =
              action.payload.updatedHealthCareScheme;
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to update health care scheme";
        }
      )
      .addCase(updateHealthCareSchemeById.rejected, (state, action) => {
        state.isUpdatingHealthCareScheme = false;
        state.error =
          action.error.message || "Failed to update health care scheme";
      })

      .addCase(updateHealthCareDoctorById.pending, (state) => {
        state.isUpdatingHealthCareDoctor = true;
        state.error = null;
      })
      .addCase(
        updateHealthCareDoctorById.fulfilled,
        (state, action: PayloadAction<IPostHealthCareDoctorResponse>) => {
          state.isUpdatingHealthCareDoctor = false;
          if (state.selectedHealthCare && action.payload.doctorRes) {
            state.selectedHealthCare.healthCareDoctor =
              action.payload.doctorRes;
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to update health care doctor";
        }
      )
      .addCase(updateHealthCareDoctorById.rejected, (state, action) => {
        state.isUpdatingHealthCareDoctor = false;
        state.error =
          action.error.message || "Failed to update health care doctor";
      })

      .addCase(updateHealthCareUserById.pending, (state) => {
        state.isUpdatingHealthCareUser = true;
        state.error = null;
      })
      .addCase(
        updateHealthCareUserById.fulfilled,
        (state, action: PayloadAction<IPostManageUserResponse>) => {
          state.isUpdatingHealthCareUser = false;
          if (state.selectedHealthCare && action.payload.manageUserRes) {
            state.selectedHealthCare.healthCareManagingUser =
              action.payload.manageUserRes;
          }
          state.error =
            action.payload.errorMessage || "Failed to update health care user";
        }
      )
      .addCase(updateHealthCareUserById.rejected, (state, action) => {
        state.isUpdatingHealthCareUser = false;
        state.error =
          action.error.message || "Failed to update health care user";
      })

      .addCase(
        updateHealthCareSchemeSpecificLoginDetailById.pending,
        (state) => {
          state.isUpdatingHealthCareSchemeSpecificLoginDetail = true;
          state.error = null;
        }
      )
      .addCase(
        updateHealthCareSchemeSpecificLoginDetailById.fulfilled,
        (
          state,
          action: PayloadAction<IPostSchemeSpecificLoginDetailsResponse>
        ) => {
          state.isUpdatingHealthCareSchemeSpecificLoginDetail = false;
          if (
            state.selectedHealthCare &&
            action.payload.schemeSpecificLoginDetailsRes
          ) {
            state.selectedHealthCare.healthCareSchemeSpecificLogin =
              action.payload.schemeSpecificLoginDetailsRes;
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to update health care scheme specific login detail";
        }
      )
      .addCase(
        updateHealthCareSchemeSpecificLoginDetailById.rejected,
        (state, action) => {
          state.isUpdatingHealthCareSchemeSpecificLoginDetail = false;
          state.error =
            action.error.message ||
            "Failed to update health care scheme specific login detail";
        }
      )

      .addCase(updateHealthCareBillingUrlById.pending, (state) => {
        state.isUpdatingHealthCareBillingUrl = true;
        state.error = null;
      })
      .addCase(
        updateHealthCareBillingUrlById.fulfilled,
        (state, action: PayloadAction<IPostBillingUrlFormResponse>) => {
          state.isUpdatingHealthCareBillingUrl = false;
          if (state.selectedHealthCare && action.payload.billingUrlFormRes) {
            state.selectedHealthCare.healthCareBillingUrl =
              action.payload.billingUrlFormRes;
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to update health care billing url";
        }
      )
      .addCase(updateHealthCareBillingUrlById.rejected, (state, action) => {
        state.isUpdatingHealthCareBillingUrl = false;
        state.error =
          action.error.message || "Failed to update health care billing url";
      })

      .addCase(updateHealthCareRequiredDocumentById.pending, (state) => {
        state.isUpdatingHealthCareRequiredDocumentById = true;
        state.error = null;
      })
      .addCase(
        updateHealthCareRequiredDocumentById.fulfilled,
        (state, action: PayloadAction<IAllCompleteHealthCare>) => {
          state.isUpdatingHealthCareRequiredDocumentById = false;
          if (
            state.selectedHealthCare &&
            action.payload.healthCareRequiredDocumentsRes &&
            action.payload.healthCareRequiredDocumentsRes.length > 0
          ) {
            state.selectedHealthCare.healthCareRequiredDocuments =
              action.payload.healthCareRequiredDocumentsRes[
                action.payload.healthCareRequiredDocumentsRes.length - 1
              ];
          }
          state.error =
            action.payload.errorMessage ||
            "Failed to update health care required document";
        }
      )
      .addCase(
        updateHealthCareRequiredDocumentById.rejected,
        (state, action) => {
          state.isUpdatingHealthCareRequiredDocumentById = false;
          state.error =
            action.error.message ||
            "Failed to update health care required document";
        }
      );
  },
});

export const {
  setHealthCareFilters,
  clearPageToHealthCareIdMap,
  setSelectedHealthCare,
  setSelectedHealthCareScheme,
  setSelectedHealthCareUser,
  setSelectedHealthCareDoctor,
  setSelectedHealthCareSchemeSpecificLoginDetail,
  setSelectedHealthCareBillingUrl,
  setSelectedHealthCareRequiredDoc,
  clearHealthCareList,
  clearSelectedHealthCare,
  clearSelectedHealthCareScheme,
  clearSelectedHealthCareDoctor,
  clearSelectedHealthCareUser,
  clearSelectedHealthCareSchemeSpecificLoginDetail,
  clearSelectedHealthCareBillingUrl,
  clearSelectedHealthCareRequiredDoc,
  clearHealthCareSchemesDoctorsUsersLoginDetailsUrlRequiredDocs,
} = healthCareSlice.actions;

export default healthCareSlice.reducer;
